import { ReactNode, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  feetToMeters,
  kilometersToMeters,
  metersToFeet,
  metersToKilometers,
  metersToMiles,
  milesToMeters
} from '@alltrails/data-formatting/distanceConverters';
import { getUnitAbbreviation } from '@alltrails/data-formatting/utils';
import { RangeSliderFilterConfig } from '@alltrails/denali/components/FilterBar';
import ExploreServiceFilters from '../../types/ExploreServiceFilters';

/**
 * Helper function to make sure the value grabbed from a search param (in meters) gets converted to a valid value for the RangeSlider.
 * The returned value is also in meters but we must first convert the appropriate unit in case a url was generated in metric then used in standard, or vice versa.
 * @param meters value in meters
 * @param useMetric use metric
 */
export const getValidRangeSliderValue = (
  meters: number,
  standardValues: number[],
  standardUnit: 'foot' | 'mile',
  metricValues: number[],
  metricUnit: 'kilometer' | 'meter',
  useMetric: boolean
) => {
  const values = useMetric ? metricValues : standardValues;

  let value = 0;
  if (useMetric) {
    value = metricUnit === 'kilometer' ? metersToKilometers(meters) : meters;
  } else {
    value = Math.round(standardUnit === 'foot' ? metersToFeet(meters) : metersToMiles(meters));
  }

  const validValue = values.find(v => value <= v);
  if (validValue === undefined) {
    return undefined;
  }

  if (useMetric) {
    return metricUnit === 'kilometer' ? kilometersToMeters(validValue) : validValue;
  } else {
    return standardUnit === 'foot' ? feetToMeters(validValue) : milesToMeters(validValue);
  }
};

const useRangeSliderFilterConfig = (
  exploreServiceFilters: ExploreServiceFilters,
  onFiltersChange: (updatedFilters: ExploreServiceFilters) => void,
  exploreServiceFilterKey: Extract<keyof ExploreServiceFilters, 'duration' | 'elevationGain' | 'highestPoint' | 'length'>,
  label: ReactNode,
  standardValues: number[],
  standardUnit: 'foot' | 'mile',
  metricValues: number[],
  metricUnit: 'kilometer' | 'meter',
  useMetric: boolean
): RangeSliderFilterConfig => {
  const intl = useIntl();

  const values = useMetric ? metricValues : standardValues;
  const minValue = values[0];
  const maxValue = values[values.length - 1];

  const lowerValue = useMemo(() => {
    if (exploreServiceFilters[exploreServiceFilterKey]?.min !== undefined) {
      const minMeters = exploreServiceFilters[exploreServiceFilterKey]?.min;
      if (useMetric) {
        return metricUnit === 'kilometer' ? metersToKilometers(minMeters) : minMeters;
      }
      return Math.round(standardUnit === 'foot' ? metersToFeet(minMeters) : metersToMiles(minMeters));
    }
    return minValue;
  }, [exploreServiceFilterKey, exploreServiceFilters, metricUnit, minValue, standardUnit, useMetric]);

  const upperValue = useMemo(() => {
    if (exploreServiceFilters[exploreServiceFilterKey]?.max !== undefined) {
      const maxMeters = exploreServiceFilters[exploreServiceFilterKey]?.max;
      if (useMetric) {
        return metricUnit === 'kilometer' ? metersToKilometers(maxMeters) : maxMeters;
      }
      return Math.round(standardUnit === 'foot' ? metersToFeet(maxMeters) : metersToMiles(maxMeters));
    }
    return maxValue;
  }, [exploreServiceFilterKey, exploreServiceFilters, maxValue, metricUnit, standardUnit, useMetric]);

  const marks = useMemo(
    () =>
      values.map((value, index) => {
        let label: string | undefined = undefined;
        if (index === 0) {
          label = intl.formatNumber(value, { style: 'unit', unit: useMetric ? metricUnit : standardUnit });
        } else if (index === values.length - 1) {
          label = `${intl.formatNumber(value, { style: 'unit', unit: useMetric ? metricUnit : standardUnit })}+`;
        }
        return { value, label };
      }),
    [intl, metricUnit, standardUnit, useMetric, values]
  );

  const formatValues = useCallback(
    (v1: number, v2: number) => {
      if (v1 === minValue && v2 === maxValue) {
        return intl.formatMessage({ defaultMessage: 'Any' });
      } else if (v2 === maxValue) {
        return `${intl.formatNumber(v1, { style: 'unit', unit: useMetric ? metricUnit : standardUnit })}+`;
      }
      const showPlus = v2 === maxValue;
      return `${intl.formatNumber(v1)}–${intl.formatNumber(v2)} ${getUnitAbbreviation(intl, useMetric ? metricUnit : standardUnit)}${showPlus ? '+' : ''}`;
    },
    [intl, maxValue, metricUnit, minValue, standardUnit, useMetric]
  );

  const valueToMeters = useCallback(
    (value: number) => {
      if (useMetric) {
        return metricUnit === 'kilometer' ? kilometersToMeters(value) : value;
      }
      return standardUnit === 'foot' ? feetToMeters(value) : milesToMeters(value);
    },
    [metricUnit, standardUnit, useMetric]
  );

  const onChange = useCallback(
    (values?: [number, number]) => {
      const newFilters = { ...exploreServiceFilters };
      delete newFilters[exploreServiceFilterKey];
      if (values) {
        const [v1, v2] = values;
        if (v1 === v2) {
          // Don't let users select no range since this is extremely likely to return no results
          return;
        }
        if (v1 === minValue && v2 === maxValue) {
          // Do nothing, act like there is no applied filter
        } else if (v1 === minValue) {
          newFilters[exploreServiceFilterKey] = { max: valueToMeters(v2) };
        } else if (v2 === maxValue) {
          newFilters[exploreServiceFilterKey] = { min: valueToMeters(v1) };
        } else {
          newFilters[exploreServiceFilterKey] = { min: valueToMeters(v1), max: valueToMeters(v2) };
        }
      }
      onFiltersChange(newFilters);
    },
    [exploreServiceFilterKey, exploreServiceFilters, maxValue, minValue, onFiltersChange, valueToMeters]
  );

  const isDefault = lowerValue === minValue && upperValue === maxValue;

  const clear = useMemo(() => {
    if (isDefault) {
      return undefined;
    }
    return () => onChange(undefined);
  }, [isDefault, onChange]);

  return {
    type: 'range-slider',
    id: exploreServiceFilterKey,
    filterChip: {
      onClear: clear,
      selectedItems: isDefault ? undefined : [formatValues(lowerValue, upperValue)],
      unselectedText: label
    },
    rangeSlider: {
      formatHeaderValue: ([v1, v2]) => formatValues(v1, v2),
      label,
      marks,
      onChange,
      value: [lowerValue, upperValue]
    }
  };
};

export default useRangeSliderFilterConfig;
